import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, timeout } from 'rxjs/operators';

import { bmxurl } from '../../globals';
import { host_returndata } from '../../types/host_returndata';
import { VenueLocale } from './venue-locale';

// The shape venuesettings actually returns. types/venuesettings.ts names this
// array `venuesettings`, but the API sends `venueSettings` and every caller in
// the app reads it off an `any`, so the declared name has never been exercised.
// Typing it here keeps this file honest without renaming a shared interface that
// nine other call sites cast to.
interface VenueSettingsResponse {
  succeeded: boolean;
  data: { venueSettings: { name: string; value: string }[] } | null;
}

// The languages that have a file in public/i18n. Anything else a venue is
// configured with falls back to English rather than rendering raw keys. Adding a
// language here without adding public/i18n/<code>.json would show raw keys, so
// i18n.spec.ts checks the two stay in step.
export const SUPPORTED_LANGUAGES = ['en', 'fr', 'de', 'es'];

export const DEFAULT_LANGUAGE = 'en';

// Routes that carry the venue's host name as their first parameter — see
// app.routes.ts. The remaining routes identify the venue another way:
// /payment carries a JWT whose payload holds the venue id, /subscriptions takes
// it as a query parameter, and the home route has no venue at all.
const VENUE_SLUG_ROUTES = new Set([
  'booking',
  'buymembership',
  'buyvoucher',
  'completewaiver',
  'events',
  'managebooking',
  'managewaiver',
  'terms',
]);

// A venue that answers slowly must not hold the page hostage. Past this the app
// boots in English; the alternative is a white screen for as long as the API
// takes to fail.
const LOOKUP_TIMEOUT_MS = 4000;

/** How the venue was identified in the URL, if it was. */
export interface VenueRef {
  slug?: string;
  venueId?: string;
}

/**
 * Maps a venue's Culture setting onto one of the languages we ship.
 *
 * Culture holds a full locale ("en-GB", "fr-FR") because it also drives date
 * formatting, so only the language part is significant here. The string checks
 * mirror the ones the booking wizard already makes: this setting comes back as
 * the literal text "null" or "undefined" for some venues, not as a missing key.
 */
export function languageFromCulture(culture: string | null | undefined): string {
  const raw = (culture ?? '').trim();

  if (raw === '' || raw === 'null' || raw === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  const code = raw.toLowerCase().split(/[-_]/)[0];

  return SUPPORTED_LANGUAGES.includes(code) ? code : DEFAULT_LANGUAGE;
}

/**
 * The locale to hand Stripe Elements, so the card form matches the rest of the page.
 *
 * Stripe defaults this to 'auto', which follows the *browser's* language - a
 * French venue's payment form still read "Card number" and "Expiry date" for a
 * customer on an English browser. The language codes we ship ('en', 'fr', 'de',
 * 'es') are valid Stripe locales as they stand.
 *
 * Note this only affects Stripe's own labels and error text. Postal code
 * validation stays tied to the card's billing country, by Stripe's design.
 */
export function stripeLocale(translate: TranslateService): string {
  return translate.currentLang || translate.getDefaultLang() || DEFAULT_LANGUAGE;
}

/**
 * Works out which venue the current URL belongs to, without the router.
 *
 * This runs during bootstrap, before the router has parsed anything, so the
 * location is read directly. Returning an empty object means "no venue" and the
 * caller defaults the language.
 */
export function venueRefFromLocation(pathname: string, search: string): VenueRef {
  const segments = pathname.split('/').filter(s => s !== '');
  const route = segments[0] ?? '';
  const params = new URLSearchParams(search);

  if (VENUE_SLUG_ROUTES.has(route) && segments[1]) {
    return { slug: decodeURIComponent(segments[1]) };
  }

  if (route === 'subscriptions') {
    const venueId = params.get('venueid');
    return venueId ? { venueId } : {};
  }

  if (route === 'payment') {
    // The payment screen has no venue in its path; it decodes the same token to
    // find the venue id itself. Reading it here avoids an extra host lookup.
    return { venueId: venueIdFromPaymentToken(params.get('token')) ?? undefined };
  }

  return {};
}

/** Pulls the venue id out of a payment token, or null if it cannot be read. */
function venueIdFromPaymentToken(token: string | null): string | null {
  if (!token) {
    return null;
  }

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload?.v === 'string' && payload.v !== '' ? payload.v : null;
  } catch {
    // A malformed token is the payment screen's problem to report, not ours.
    return null;
  }
}

/**
 * Resolves the venue's language and applies it, before the app renders.
 *
 * Returns an observable that always completes, whatever the API does — this is
 * wired into an app initializer, so an error escaping here would stop the app
 * booting at all. Every failure path settles on English.
 */
export function applyVenueLanguage(
  http: HttpClient,
  translate: TranslateService,
  venueLocale: VenueLocale,
): Observable<unknown> {
  const ref = typeof window === 'undefined'
    ? {}
    : venueRefFromLocation(window.location.pathname, window.location.search);

  return applyLanguageForVenue(http, translate, ref, venueLocale);
}

/**
 * The half of applyVenueLanguage that does the lookups, split out so it can be
 * driven with a known venue — under Karma window.location is the test runner's
 * own URL, which names no venue.
 */
export function applyLanguageForVenue(
  http: HttpClient,
  translate: TranslateService,
  ref: VenueRef,
  venueLocale?: VenueLocale,
): Observable<unknown> {
  return venueIdFor(http, ref).pipe(
    switchMap(venueId => venueId ? cultureFor(http, venueId) : of(null)),
    timeout(LOOKUP_TIMEOUT_MS),
    catchError(() => of(null)),
    // The Culture string is kept as well as the language it maps to: the wording
    // comes from the language file, but dates are formatted against the full
    // culture so a French venue also gets French date ordering.
    switchMap(culture => {
      const language = languageFromCulture(culture);
      venueLocale?.set(culture, language);
      return translate.use(language);
    }),
  );
}

/** The venue id, either straight from the URL or via a host lookup. */
function venueIdFor(http: HttpClient, ref: VenueRef): Observable<string | null> {
  if (ref.venueId) {
    return of(ref.venueId);
  }

  if (!ref.slug) {
    return of(null);
  }

  return http.get<host_returndata>(bmxurl + '/host/' + ref.slug).pipe(
    map(response => response?.succeeded && response.data ? response.data.venueid : null),
    catchError(() => of(null)),
  );
}

/** The venue's Culture setting, or null if it is not set or cannot be read. */
function cultureFor(http: HttpClient, venueId: string): Observable<string | null> {
  return http.get<VenueSettingsResponse>(bmxurl + 'venuesettings/' + venueId).pipe(
    map(response => {
      if (!response?.succeeded || !response.data?.venueSettings) {
        return null;
      }

      const setting = response.data.venueSettings.find(s => s.name === 'Culture');
      return setting ? setting.value : null;
    }),
    catchError(() => of(null)),
  );
}
